import React, { useEffect, useRef, useCallback, useState } from "react";

/**
 * YouTubePlayer — direct iframe embed with URL-based autoplay.
 *
 * Why URL-based autoplay:
 *   Browsers reject programmatic play() calls that arrive through
 *   postMessage even when the parent tab has a user gesture, because the
 *   activation doesn't propagate to a cross-origin iframe reliably. The
 *   ONE thing every browser respects is `autoplay=1` in the iframe URL
 *   itself, provided the iframe is muted (`mute=1`). So the moment we
 *   have a real videoId, we point the iframe at `.../embed/VID?autoplay=1
 *   &mute=1`. The video starts playing (muted) immediately. When the
 *   user hits the PLAY button, we simply unmute via postMessage — which
 *   IS allowed because audio is already flowing on this origin.
 *
 * We deliberately skip YouTube's `iframe_api.js` (which uses eval/new
 * Function() and is killed by strict CSPs on installed PWAs).
 */

let seq = 0;
const PLACEHOLDER = "dQw4w9WgXcQ"; // a real, embeddable video for the "cold" iframe

function buildEmbedSrc(videoId, autoplay) {
  const origin = encodeURIComponent(window.location.origin);
  const vid = videoId || PLACEHOLDER;
  return (
    `https://www.youtube.com/embed/${vid}` +
    `?enablejsapi=1` +
    `&mute=1` +
    (autoplay ? `&autoplay=1` : `&autoplay=0`) +
    `&controls=0` +
    `&playsinline=1` +
    `&modestbranding=1` +
    `&rel=0` +
    `&iv_load_policy=3` +
    `&disablekb=1` +
    `&fs=0` +
    `&origin=${origin}`
  );
}

export default function YouTubePlayer({
  videoId,
  onReady,
  onStateChange,
  onTime,
  onError,
  onDiag,
  hidden = true,
  slot = 0,
}) {
  const iframeRef = useRef(null);
  const idRef = useRef(`yt-${slot}-${++seq}`);
  const stateRef = useRef({
    ready: false,
    playerState: -1,
    currentTime: 0,
    duration: 0,
    muted: true,
    volume: 100,
    rate: 1,
    videoId: null,
    userWantsAudio: false, // true once user has tapped PLAY at least once
    pending: [],
  });
  const controllerRef = useRef(null);
  // Keep the latest callbacks in a ref so the postMessage listener can be
  // registered ONCE with stable identity. Otherwise every parent re-render
  // (which passes fresh inline arrow callbacks) tears down and re-adds
  // the listener, and YouTube's onReady message can fall through the gap.
  // Initialize empty; sync with the latest props on every render.
  const cbRef = useRef({});
  cbRef.current = { onReady, onStateChange, onTime, onError, onDiag };
  // We keep the iframe src in state so React re-renders when we swap videos
  const [iframeSrc, setIframeSrc] = useState(() => buildEmbedSrc(videoId, !!videoId));

  const post = useCallback((func, args = []) => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentWindow) return false;
    try {
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: "command", func, args, id: idRef.current, channel: "widget" }),
        "*"
      );
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  const flush = useCallback(() => {
    const q = stateRef.current.pending;
    stateRef.current.pending = [];
    q.forEach(({ func, args }) => post(func, args));
  }, [post]);

  const enqueue = useCallback(
    (func, args = []) => {
      if (stateRef.current.ready) return post(func, args);
      stateRef.current.pending.push({ func, args });
      return true;
    },
    [post]
  );

  const swapVideo = useCallback((newVideoId, autoplay) => {
    stateRef.current.videoId = newVideoId;
    stateRef.current.ready = false;
    stateRef.current.playerState = -1;
    stateRef.current.pending = [];
    setIframeSrc(buildEmbedSrc(newVideoId, autoplay));
    cbRef.current.onDiag && cbRef.current.onDiag(autoplay ? "loading + autoplay" : "loading");
  }, []);

  // Build the controller once
  if (!controllerRef.current) {
    controllerRef.current = {
      // playVideo strategy:
      //  - state=2 (paused): postMessage playVideo — resume from position.
      //  - Once ANY deck in this tab has already played (audio session
      //    is unlocked for the origin), use postMessage playVideo — no
      //    iframe reload, so the sibling deck keeps its audio focus.
      //  - Only the very FIRST play on the tab uses URL-reload autoplay,
      //    which is the only path browsers allow for a cold audio start.
      playVideo: () => {
        stateRef.current.userWantsAudio = true;
        const vid = stateRef.current.videoId;
        if (!vid) return false;
        const st = stateRef.current.playerState;
        const audioUnlocked = typeof window !== "undefined" && window.__djAudioUnlocked;
        if (st === 2 || audioUnlocked) {
          // Resume / start via postMessage — no iframe reload
          post("playVideo");
          post("unMute");
          post("setVolume", [stateRef.current.volume]);
          // Safety net: if state hasn't changed within 1500ms, fall back
          // to a URL reload — the browser may have blocked the message.
          setTimeout(() => {
            if (stateRef.current.playerState !== 1 && stateRef.current.playerState !== 3) {
              swapVideo(vid, true);
            }
          }, 1500);
          return true;
        }
        // First cold start on this tab — URL reload with autoplay=1.
        // Mark the origin as unlocked so subsequent plays can use
        // postMessage and keep the other deck alive.
        try { window.__djAudioUnlocked = true; } catch (_setErr) { /* window may be missing */ }
        swapVideo(vid, true);
        return true;
      },
      pauseVideo: () => enqueue("pauseVideo"),
      stopVideo: () => enqueue("stopVideo"),
      seekTo: (sec, allowSeekAhead = true) => enqueue("seekTo", [sec, allowSeekAhead]),
      cueVideoById: (id) => {
        const vid = typeof id === "object" ? id.videoId : id;
        stateRef.current.userWantsAudio = false;
        stateRef.current.videoId = vid;
        if (stateRef.current.ready && iframeRef.current) {
          post("cueVideoById", [{ videoId: vid, startSeconds: 0 }]);
          cbRef.current.onDiag && cbRef.current.onDiag("cued");
        } else {
          swapVideo(vid, false);
        }
      },
      loadVideoById: (id) => {
        const vid = typeof id === "object" ? id.videoId : id;
        stateRef.current.userWantsAudio = true;
        swapVideo(vid, true);
      },
      mute: () => {
        stateRef.current.muted = true;
        return enqueue("mute");
      },
      unMute: () => {
        stateRef.current.muted = false;
        return enqueue("unMute");
      },
      setVolume: (v) => {
        stateRef.current.volume = v;
        return enqueue("setVolume", [v]);
      },
      setPlaybackRate: (r) => {
        stateRef.current.rate = r;
        return enqueue("setPlaybackRate", [r]);
      },
      getPlayerState: () => stateRef.current.playerState,
      getCurrentTime: () => stateRef.current.currentTime,
      getDuration: () => stateRef.current.duration,
      getPitch: () => stateRef.current.rate,
      destroy: () => {},
    };
  }

  // Listen for postMessage events from the iframe.
  // Empty deps → this effect runs ONCE for the component lifetime. All
  // callbacks are accessed via cbRef.current so they stay fresh without
  // requiring the listener to be torn down and re-added.
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.origin || !event.origin.includes("youtube")) return;
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      let data = event.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch (_e) { return; }
      }
      if (!data || !data.event) return;

      const cbs = cbRef.current;

      if (data.event === "onReady") {
        stateRef.current.ready = true;
        cbs.onDiag && cbs.onDiag("ready");
        try {
          iframeRef.current.contentWindow.postMessage(
            JSON.stringify({ event: "listening", id: idRef.current, channel: "widget" }),
            "*"
          );
        } catch (_e) { /* iframe gone */ }
        cbs.onReady && cbs.onReady(controllerRef.current);
        flush();
      } else if (data.event === "infoDelivery" && data.info) {
        const oldState = stateRef.current.playerState;
        if (typeof data.info.currentTime === "number") stateRef.current.currentTime = data.info.currentTime;
        if (typeof data.info.duration === "number") stateRef.current.duration = data.info.duration;
        if (typeof data.info.playerState === "number") stateRef.current.playerState = data.info.playerState;
        if (typeof data.info.muted === "boolean") stateRef.current.muted = data.info.muted;
        cbs.onTime && cbs.onTime(stateRef.current.currentTime, stateRef.current.duration);
        if (
          typeof data.info.playerState === "number" &&
          data.info.playerState !== oldState
        ) {
          if (data.info.playerState === 1 && stateRef.current.userWantsAudio) {
            post("unMute");
            post("setVolume", [stateRef.current.volume]);
          }
          cbs.onStateChange && cbs.onStateChange(data.info.playerState, controllerRef.current);
        }
      } else if (data.event === "onStateChange") {
        stateRef.current.playerState = data.info;
        if (data.info === 1 && stateRef.current.userWantsAudio) {
          post("unMute");
          post("setVolume", [stateRef.current.volume]);
        }
        cbs.onStateChange && cbs.onStateChange(data.info, controllerRef.current);
      } else if (data.event === "onError") {
        cbs.onDiag && cbs.onDiag(`error ${data.info}`);
        cbs.onError && cbs.onError(data.info);
      }
    };
    window.addEventListener("message", handleMessage);

    // CRITICAL: hand the controller to the parent immediately, without
    // waiting for YouTube's onReady message. `playerRef.current` in Deck
    // must be non-null the instant the user is able to tap PLAY.
    // Any commands issued before YT confirms ready are queued via
    // enqueue() and flushed when the real onReady arrives.
    try {
      cbRef.current.onReady && cbRef.current.onReady(controllerRef.current);
    } catch (_e) { /* callback threw */ }

    // Send an immediate listening ping so if the iframe already fired
    // onReady before our listener registered, YouTube will re-emit state
    // via infoDelivery once we start listening.
    const sendPing = () => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        try {
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: "listening", id: idRef.current, channel: "widget" }),
            "*"
          );
        } catch (_e) { /* not addressable yet */ }
      }
    };
    sendPing();
    // Continue pinging until we get onReady (in case the iframe wasn't
    // addressable on the first try)
    const pingTimer = setInterval(() => {
      if (stateRef.current.ready) return;
      sendPing();
    }, 500);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearInterval(pingTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIframeLoad = useCallback(() => {
    if (cbRef.current.onDiag) cbRef.current.onDiag("iframe loaded");
    // Some browsers/versions don't propagate the initial `onReady`
    // postMessage from a freshly-loaded YouTube iframe. Actively poke
    // the iframe as soon as it finishes loading so YT starts streaming
    // infoDelivery events even without a proper onReady.
    setTimeout(() => {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        try {
          iframe.contentWindow.postMessage(
            JSON.stringify({ event: "listening", id: idRef.current, channel: "widget" }),
            "*"
          );
        } catch (_e) { /* iframe navigating */ }
      }
    }, 150);
  }, []);

  const hiddenStyle = hidden
    ? {
        // Corner-pinned 200x200 iframe. Opacity 0.18 keeps it clearly
        // "visible" to browser autoplay heuristics (0.02 was treated
        // as invisible and autoplay was refused). pointer-events:none
        // so the deck controls receive taps. z-index:-1 puts it behind
        // ALL deck content (buttons, waveform, text) which stay crisp.
        position: "absolute",
        right: 8,
        bottom: 8,
        width: 200,
        height: 200,
        opacity: 0.18,
        pointerEvents: "none",
        zIndex: -1,
        overflow: "hidden",
        borderRadius: 12,
        filter: "saturate(0.75) blur(0.5px)",
      }
    : {};

  return (
    <div style={hiddenStyle} aria-hidden="true" data-yt-slot={slot}>
      <iframe
        key={iframeSrc}
        ref={iframeRef}
        title={`yt-${slot}`}
        src={iframeSrc}
        width="200"
        height="200"
        frameBorder="0"
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen={false}
        onLoad={handleIframeLoad}
        style={{ display: "block", width: 200, height: 200, border: 0 }}
        data-testid={`yt-iframe-${slot}`}
      />
    </div>
  );
}
